import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  IconButton,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";

import { useDispatch, useSelector } from "react-redux";
import { setDatas, setFilters, updateMarketDatas } from "state";
import { toastError } from "services/apiService";

const getRowId = (product) => product?.Identifiant;

const tableColumns = [
  { key: "Code", label: "Code pièce" },
  { key: "referenceOEM", label: "Réf. OEM" },
  { key: "Libelle", label: "Libellé" },
  { key: "MarqueVEH", label: "Marque" },
  { key: "ModeleVEH", label: "Modele" },
  { key: "PrixVenteTTC", label: "Prix TTC" },
  { key: "PrixVenteTotalTTC", label: "Prix Total" },
  { key: "DateStockage", label: "En stock depuis" },
  { key: "dateSynchro", label: "Dernière synchro GS" },
  { key: "Qualite", label: "Qualité estimée" },
  { key: "minMarketPrice", label: "Prix min marché" },
  { key: "maxMarketPrice", label: "Prix max marché" },
  { key: "medianMarketPrice", label: "Prix median marché" },
  { key: "carecoPrice", label: "Prix CARECO" },
  { key: "marketResultsCount", label: "Résultats prix" },
  { key: "recommendedPrice", label: "Prix conseillé" },
  { key: "newPrice", label: "Nouveau Prix", type: "input" },
];

const marketPriceColumnKeys = [
  "minMarketPrice",
  "maxMarketPrice",
  "medianMarketPrice",
  "carecoPrice",
];

const formatStockageDuration = (rawValue) => {
  if (typeof rawValue !== "string" || rawValue.length < 8) {
    return "-";
  }

  const date = new Date(
    `${rawValue.slice(0, 4)}-${rawValue.slice(4, 6)}-${rawValue.slice(6, 8)}`
  );

  if (isNaN(date.getTime())) {
    return "-";
  }

  const diffMs = Date.now() - date.getTime();
  if (diffMs < 0) {
    return "-";
  }

  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays >= 365) {
    const years = Math.floor(diffDays / 365);
    return `${years} an${years > 1 ? "s" : ""}`;
  }

  if (diffDays >= 30) {
    const months = Math.floor(diffDays / 30);
    return `${months} mois`;
  }

  return `${diffDays} j`;
};

const formatOfferPrice = (offer) =>
  Number.isFinite(offer?.prixNum) ? `${offer.prixNum.toFixed(2)} €` : "-";

const ProductTable = ({
  isLoading = false,
  isPriceSyncLoading = false,
  onChangeSelection = () => {},
  onValidate = () => {},
}) => {
  const [selectedIds, setSelectedIds] = useState([]);
  const [expandedIds, setExpandedIds] = useState([]);
  const [marketPriceLoading, setMarketPriceLoading] = useState(false);
  const filters = useSelector((state) => state.filters);
  const datas = useSelector((state) => state.datas);
  const marketData = useSelector((state) => state.marketData);
  const dispatch = useDispatch();

  const rowIds = useMemo(
    () => datas?.pieces?.map(getRowId).filter(Boolean),
    [datas?.pieces]
  );

  const rowIdSet = useMemo(() => new Set(rowIds), [rowIds]);
  const allSelected =
    rowIds?.length > 0 && rowIds?.every((id) => selectedIds.includes(id));
  const isIndeterminate = selectedIds.length > 0 && !allSelected;

  useEffect(() => {
    onChangeSelection(selectedIds);
  }, [selectedIds, onChangeSelection]);

  useEffect(() => {
    setSelectedIds((prev) => prev.filter((id) => rowIdSet.has(id)));
    setExpandedIds((prev) => prev.filter((id) => rowIdSet.has(id)));
  }, [rowIdSet]);

  const [progressCount, setProgressCount] = useState(0);
  const [isProgressOpen, setIsProgressOpen] = useState(false);

  const handleSearchMarketPrice = async () => {
    if (selectedIds.length === 0) return;

    setIsProgressOpen(true);
    setProgressCount(0);
    setMarketPriceLoading(true);

    const token = localStorage.getItem("token");
    const eventSource = new EventSource(
      `${
        process.env.REACT_APP_API_URL
      }/atemo/getMarketPricesStream?selectedIds=${selectedIds.join(
        ","
      )}&token=${token}`
    );

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        if (data.type === "progress") {
          setProgressCount(data.current);
        } else if (data.type === "complete") {
          dispatch(updateMarketDatas({ marketData: data.results }));
          eventSource.close();
          setIsProgressOpen(false);
          setMarketPriceLoading(false);
          setProgressCount(0);
        } else if (data.type === "error") {
          console.error("SSE Error:", data.message);
          toastError("Erreur lors de la récupération des prix");
          eventSource.close();
          setIsProgressOpen(false);
          setMarketPriceLoading(false);
        }
      } catch (e) {
        console.error("Error parsing SSE data:", e);
      }
    };

    eventSource.onerror = (err) => {
      console.error("EventSource failed:", err);
      eventSource.close();
      setIsProgressOpen(false);
      setMarketPriceLoading(false);
      // Only show error if we didn't complete normally (sometimes onerror fires on close)
      if (progressCount < selectedIds.length) {
        toastError("Erreur de connexion au serveur");
      }
    };
  };

  const handleApplyAllRecommended = () => {
    // apply recommendedPrice to newPrice for selected products
    let newProductDatas = {
      ...datas,
      pieces: datas.pieces.map((p) =>
        selectedIds.includes(p.Identifiant)
          ? { ...p, newPrice: p.recommendedPrice }
          : { ...p }
      ),
    };

    dispatch(setDatas({ datas: newProductDatas }));
  };

  const handleValidate = () => {
    if (!onValidate) return;

    const selectedProducts = datas.pieces.filter((p) =>
      selectedIds.includes(p.Identifiant)
    );

    const updatedProducts = selectedProducts.map((p) => {
      const pieceMarketData = marketData?.[getRowId(p)];
      if (pieceMarketData) {
        return {
          ...p,
          newPrice: p.newPrice || `${pieceMarketData.recommendedPrice}`,
        };
      }
      return p;
    });

    onValidate(updatedProducts);
  };

  const handleToggle = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id)
        ? prev.filter((selectedId) => selectedId !== id)
        : [...prev, id]
    );
  };

  const handleToggleDetails = (id) => {
    setExpandedIds((prev) =>
      prev.includes(id)
        ? prev.filter((expandedId) => expandedId !== id)
        : [...prev, id]
    );
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedIds(rowIds);
    } else {
      setSelectedIds([]);
    }
  };

  const handleNewPriceChange = (index, newPrice) => {
    let newProductDatas = {
      ...datas,
      pieces: datas?.pieces?.map((p) => ({ ...p })),
    };
    newProductDatas.pieces[index].newPrice = newPrice;
    dispatch(setDatas({ datas: newProductDatas }));
  };

  const renderGShoppingDetails = (results) => {
    if (!Array.isArray(results) || results.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          Aucun résultat Google Shopping pris en compte.
        </Typography>
      );
    }

    return (
      <Box sx={{ p: 2, bgcolor: "action.hover" }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Résultats Google Shopping pris en compte ({results.length})
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Produit</TableCell>
              <TableCell>Vendeur</TableCell>
              <TableCell>Prix affiché</TableCell>
              <TableCell>Livraison</TableCell>
              <TableCell>Lien</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {results.map((result, resultIndex) => {
              const link = result.link || result.url;
              return (
                <TableRow key={`${result.url || result.titre}-${resultIndex}`}>
                  <TableCell sx={{ maxWidth: 360 }}>
                    <Typography variant="body2">{result.titre || "-"}</Typography>
                  </TableCell>
                  <TableCell>{result.seller || result.magasin || "-"}</TableCell>
                  <TableCell>
                    {result.displayedPrice || result.prix || formatOfferPrice(result)}
                  </TableCell>
                  <TableCell>{result.shipping || "-"}</TableCell>
                  <TableCell>
                    {link && link !== "—" ? (
                      <Link
                        href={link}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(event) => event.stopPropagation()}
                      >
                        Ouvrir
                      </Link>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    );
  };

  const renderBody = () => {
    if (isLoading) {
      return (
        <TableRow>
          <TableCell colSpan={6} align="center">
            Chargement des produits...
          </TableCell>
        </TableRow>
      );
    }

    if (!datas?.pieces?.length) {
      return (
        <TableRow>
          <TableCell colSpan={6} align="center">
            Aucun produit à afficher pour le moment.
          </TableCell>
        </TableRow>
      );
    }

    return datas?.pieces.map((product, index) => {
      const id = getRowId(product);
      const isChecked = selectedIds.includes(id);
      const isExpanded = expandedIds.includes(id);
      let refOe = product?.ReferenceOE;
      if (typeof refOe === "object" && refOe !== null) {
        refOe = "-";
      }
      const pieceMarketData = marketData?.[id];
      if (pieceMarketData) {
        product = {
          ...product,
          ...pieceMarketData,
        };
      }
      const gShoppingResults = Array.isArray(product.gShoppingResultsConsidered)
        ? product.gShoppingResultsConsidered
        : [];
      const hasGShoppingDetails = gShoppingResults.length > 0;

      return (
        <React.Fragment key={id}>
          <TableRow
            hover
            selected={isChecked}
            onClick={() => handleToggle(id)}
            sx={{ cursor: "pointer" }}
          >
            <TableCell padding="checkbox">
              <IconButton
                size="small"
                disabled={!hasGShoppingDetails}
                onClick={(event) => {
                  event.stopPropagation();
                  if (hasGShoppingDetails) {
                    handleToggleDetails(id);
                  }
                }}
              >
                {isExpanded ? (
                  <KeyboardArrowDownIcon fontSize="small" />
                ) : (
                  <KeyboardArrowRightIcon fontSize="small" />
                )}
              </IconButton>
            </TableCell>
            <TableCell padding="checkbox">
              <Checkbox
                checked={isChecked}
                onClick={(event) => event.stopPropagation()}
                onChange={() => handleToggle(id)}
              />
            </TableCell>
            {tableColumns.map((column) => {
              let data = product[column.key];

              if (column.key === "referenceOEM") {
                data = product.referenceOEM || refOe;
              }

              if (column.type === "input") {
                return (
                  <TableCell
                    key={column.key}
                    padding="normal"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <TextField
                      value={product.newPrice ?? product.recommendedPrice}
                      placeholder="Nouveau prix"
                      type="number"
                      min="5"
                      onChange={(event) => {
                        handleNewPriceChange(index, event.target.value);
                      }}
                      slotProps={{
                        htmlInput: { inputMode: "decimal", min: 5, step: "1" },
                      }}
                    />
                  </TableCell>
                );
              }

              if (column.key === "DateStockage") {
                data = formatStockageDuration(data);
              } else if (column.key === "dateSynchro" && data) {
                const date = new Date(data);
                data = isNaN(date.getTime()) ? "-" : date.toLocaleDateString();
              } else if (
                [
                  "PrixVenteTTC",
                  "PrixVenteTotalTTC",
                  "recommendedPrice",
                ].includes(column.key) &&
                typeof data === "number"
              ) {
                data = `${data.toFixed(2)} €`;
              } else if (marketPriceColumnKeys.includes(column.key)) {
                const offer = data;
                const canApplyOffer = Number.isFinite(offer?.prixNum);
                data = formatOfferPrice(offer);

                return (
                  <TableCell
                    padding="normal"
                    key={column.key}
                    onClick={(event) => {
                      event.stopPropagation();
                      if (canApplyOffer) {
                        handleNewPriceChange(index, offer.prixNum.toFixed(2));
                      }
                    }}
                    sx={{
                      color: canApplyOffer ? "primary.main" : "inherit",
                      cursor: canApplyOffer ? "pointer" : "default",
                      fontWeight: canApplyOffer ? 600 : "inherit",
                      whiteSpace: "nowrap",
                    }}
                    title={
                      canApplyOffer
                        ? "Utiliser ce prix comme nouveau prix"
                        : undefined
                    }
                  >
                    {data}
                  </TableCell>
                );
              } else if (typeof data === "object" && data !== null) {
                data = "-";
              }

              return (
                <TableCell padding="normal" key={column.key}>
                  {data || "-"}
                </TableCell>
              );
            })}
          </TableRow>
          <TableRow>
            <TableCell
              colSpan={tableColumns.length + 2}
              sx={{ p: 0, borderBottom: isExpanded ? undefined : 0 }}
            >
              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                {renderGShoppingDetails(gShoppingResults)}
              </Collapse>
            </TableCell>
          </TableRow>
        </React.Fragment>
      );
    });
  };

  return (
    <Box>
      {/* Progress Dialog */}
      {isProgressOpen && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            bgcolor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <Paper
            sx={{
              p: 4,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
              minWidth: 300,
            }}
          >
            <Typography variant="h6">Recherche des prix marché...</Typography>
            <Box sx={{ position: "relative", display: "inline-flex" }}>
              <Typography
                variant="h4"
                component="div"
                color="text.secondary"
              >{`${progressCount} / ${selectedIds.length}`}</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Veuillez patienter
            </Typography>
          </Paper>
        </Box>
      )}

      {selectedIds && selectedIds.length > 0 && (
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          margin={"8px 0"}
          justifyContent="flex-end"
        >
          <Button
            variant="outlined"
            onClick={handleSearchMarketPrice}
            loading={marketPriceLoading}
          >
            Google shopping price
          </Button>
          {marketData && (
            <Button variant="outlined" onClick={handleApplyAllRecommended}>
              Appliquer le prix recommandé
            </Button>
          )}
          <Button
            variant="contained"
            onClick={handleValidate}
            loading={isPriceSyncLoading}
          >
            Valider
          </Button>
        </Stack>
      )}
      <TableContainer component={Paper} sx={{ maxHeight: 440 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" variant="head" />
              <TableCell padding="checkbox" variant="head">
                <Checkbox
                  indeterminate={isIndeterminate}
                  checked={allSelected}
                  onChange={handleSelectAll}
                />
              </TableCell>
              {tableColumns.map((column) => (
                <TableCell
                  key={column.key}
                  variant="head"
                  sx={{
                    minWidth: column.key === "newPrice" ? 140 : 90,
                    maxWidth: 200,
                    whiteSpace: "nowrap",
                  }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>{renderBody()}</TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[20, 50, 100]}
        onRowsPerPageChange={(event) => {
          const newFilters = {
            ...filters,
            rowsPerPage: event.target.value || 20,
            page: 0,
          };
          dispatch(setFilters({ filters: newFilters }));
        }}
        component="div"
        count={datas.nbTotalPieces ?? 0}
        rowsPerPage={filters?.rowsPerPage ?? 20}
        page={filters?.page ?? 0}
        onPageChange={(_, newPage) => {
          const newFilters = { ...filters, page: newPage };
          dispatch(setFilters({ filters: newFilters }));
        }}
      />

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="body2">
          {selectedIds.length} produit{selectedIds.length > 1 ? "s" : ""}{" "}
          sélectionné{selectedIds.length > 1 ? "s" : ""}
        </Typography>
      </Box>
    </Box>
  );
};

export default ProductTable;
